
import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserRole, AgeGroup } from '@/types/user';
import { getRolePermissions, getAvailableRoles } from '@/utils/rolePermissions';
import { roles } from '@/data/roleData';
import { Crown, Star } from 'lucide-react';

interface RoleSelectorProps {
  selectedRole: UserRole;
  selectedAgeGroup: AgeGroup;
  setSelectedRole: (role: UserRole) => void;
  prefilledSupportRole?: string;
  prefilledRole?: UserRole;
}

const RoleSelector = ({ selectedRole, selectedAgeGroup, setSelectedRole, prefilledSupportRole, prefilledRole }: RoleSelectorProps) => {
  const availableRoles = getAvailableRoles(selectedAgeGroup);
  // Only show signup roles (exclude creative and support from general signup)
  const signupRoleIds: UserRole[] = ['player', 'coach', 'team-owner', 'venue', 'scorekeeper', 'fan', 'parent'];
  const availableRoleData = roles.filter(role => 
    availableRoles.includes(role.id) && signupRoleIds.includes(role.id)
  );

  // Auto-select role if pre-filled from URL or FAQ
  useEffect(() => {
    if (prefilledRole && availableRoles.includes(prefilledRole)) {
      setSelectedRole(prefilledRole);
    } else if (prefilledSupportRole === 'score_keeper' && availableRoles.includes('support')) {
      setSelectedRole('support');
    }
  }, [prefilledRole, prefilledSupportRole, availableRoles, setSelectedRole]);

  const getRoleDisplayInfo = (role: UserRole) => {
    const permissions = getRolePermissions(role, selectedAgeGroup);
    const isScoreKeeper = role === 'support' && prefilledSupportRole === 'score_keeper';
    
    return {
      available: permissions.available,
      requiresFee: permissions.requires_fee,
      requiresApproval: permissions.requires_approval,
      isScoreKeeper
    };
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-white mb-2">Choose Your Role</h3>
        <p className="text-gray-400 text-sm">Select the role that best describes your involvement in sports</p>
      </div>

      {prefilledSupportRole === 'score_keeper' && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Star className="h-5 w-5 text-yellow-400" />
            <span className="font-semibold text-yellow-400">Score Keeper Registration</span>
          </div>
          <p className="text-gray-300 text-sm">
            You're signing up to be a Score Keeper! This role comes with special recognition and premium tools.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {availableRoleData.map((role) => {
          const { available, requiresFee, requiresApproval, isScoreKeeper } = getRoleDisplayInfo(role.id);
          const isSelected = selectedRole === role.id;

          if (!available) return null;

          return (
            <Card
              key={role.id}
              className={`cursor-pointer transition-all hover:scale-105 ${
                isSelected 
                  ? isScoreKeeper 
                    ? 'border-yellow-500 bg-yellow-500/10' 
                    : 'border-sports-green bg-sports-green/10'
                  : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
              }`}
              onClick={() => setSelectedRole(role.id)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-lg ${
                    isSelected 
                      ? isScoreKeeper 
                        ? 'text-yellow-400' 
                        : 'text-sports-green'
                      : 'text-white'
                  }`}>
                    {role.title}
                    {isScoreKeeper && (
                      <Star className="inline-block ml-2 h-4 w-4 text-yellow-400" />
                    )}
                  </CardTitle>
                  <div className="flex gap-1">
                    {requiresFee && (
                      <Badge variant="outline" className="text-xs border-orange-500 text-orange-400">
                        <Crown className="w-3 h-3 mr-1" />
                        Paid
                      </Badge>
                    )}
                    {requiresApproval && (
                      <Badge variant="outline" className="text-xs border-blue-500 text-blue-400">
                        Approval Required
                      </Badge>
                    )}
                    {isScoreKeeper && (
                      <Badge className="text-xs bg-yellow-500 text-black">
                        Score Keeper
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-gray-300 text-sm mb-4">{role.description}</p>
                <div className="space-y-1">
                  {role.features.slice(0, 3).map((feature, index) => (
                    <div key={index} className="flex items-center text-xs text-gray-400">
                      <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
                      {feature}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedRole && (
        <div className="text-center">
          <Button 
            variant="outline" 
            className={`${
              getRoleDisplayInfo(selectedRole).isScoreKeeper
                ? 'border-yellow-500 text-yellow-400 hover:bg-yellow-500/10'
                : 'border-sports-green text-sports-green hover:bg-sports-green/10'
            }`}
          >
            Continue as {roles.find(r => r.id === selectedRole)?.title}
            {getRoleDisplayInfo(selectedRole).isScoreKeeper && ' (Score Keeper)'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RoleSelector;
